import { ajax, trigger, updateBlockModelCallback } from 'OK/utils/vanilla';

// Произвольная задержка для отработки анимации, чтобы не вешаться на `transionend`
var UPDATE_EXPANDABLE_DELAY = 500;
var AUTO_SAVE_DELAY = 2000;

export default class TextField {
    // кэш элементов компоненты
    el = null;
    textCnt = null;
    textInput = null;
    textOutput = null;
    expandBtn = null;
    collapseBtn = null;
    saveBtn = null;
    cancelBtn = null;
    counter = null;
    errorCnt = null;

    // Если задана строка, будут кнопки и ajax
    saveUrl = '';
    redesigned =  false;

    // Текущее состояние компонента
    state = {
        loading: false,
        readOnly: false,
        editMode: false,
        serverRendering: false,

        expandable: false,
        expanded: false,
        noexpand: false,
        autoexpand: false,
        autosave: false,
        autofocus: true,

        name: '',
        value: '',
        originValue: '',
        serverHtml: '',
        placeholder: '',
        error: '',
        logTargetOnExpand: '',

        limit: -1,
        limitThreshold: -1
    };

    constructor() {
        this._autoSave = this.save.bind(this);
    }

    /**
     * Активация виджета
     * @param {Element} el
     */
    activate(el) {
        this.el = el;
        this.saveUrl = this.el.getAttribute('data-save-url');
        this.textCnt = this.el.querySelector('.text-field_cnt');
        this.textInput = this.el.querySelector('.text-field_editor');
        this.textOutput = this.el.querySelector('.text-field_text');
        this.expandBtn = this.el.querySelector('.text-field_control.__expand');
        this.collapseBtn = this.el.querySelector('.text-field_control.__collapse');
        this.editBtn = this.el.querySelector('.text-field_control.__edit');
        this.saveBtn = this.el.querySelector('.text-field_control.__save');
        this.cancelBtn = this.el.querySelector('.text-field_control.__cancel');
        this.counter = this.el.querySelector('.text-field_counter');
        this.errorCnt = this.el.querySelector('.text-field_error');

        this.textInput.addEventListener('input', this);
        this.textInput.addEventListener('keydown', this);
        this.textInput.addEventListener('blur', this);
        this.textCnt.addEventListener('mouseup', this);
        this.textCnt.addEventListener('mouseenter', this);
        this.textCnt.addEventListener('mouseleave', this);
        this.expandBtn && this.expandBtn.addEventListener('click', this);
        this.collapseBtn && this.collapseBtn.addEventListener('click', this);
        this.editBtn && this.editBtn.addEventListener('click', this);
        this.saveBtn && this.saveBtn.addEventListener('click', this);
        this.cancelBtn && this.cancelBtn.addEventListener('click', this);

        this.updateView({
            name: this.textInput.name,
            value: this.textInput.value,
            originValue: this.textInput.value,
            serverHtml: this.textOutput.innerHTML,
            placeholder: this.textInput.getAttribute('placeholder'),
            logTargetOnExpand: this.el.getAttribute('data-expand-log-target'),

            limit: this.counter ? parseInt(this.counter.getAttribute('data-max-length'), 10) : -1,
            limitThreshold: this.counter ? parseInt(this.counter.getAttribute('data-limit-threshold'), 10) : -1,
            redesigned: this.el.getAttribute('data-redesigned') === 'true',

            readOnly: this.el.hasAttribute('data-read-only'),
            editMode: this.el.hasAttribute('data-edit-mode'),
            serverRendering: this.el.hasAttribute('data-server-rendering'),

            expandable: this.el.hasAttribute('data-expanded'),
            expanded: this.el.hasAttribute('data-expanded'),
            noexpand: this.el.hasAttribute('data-noexpand'),
            autoexpand: this.el.hasAttribute('data-autoexpand'),
            autosave: this.el.hasAttribute('data-autosave'),
            autofocus: this.el.hasAttribute('data-autofocus'),

            error: this.errorCnt.innerHTML
        });

        this._checkTextExpandable();
    }

    /**
     * Деактивация виджета
     */
    deactivate() {
        this.textInput.removeEventListener('input', this);
        this.textInput.removeEventListener('keydown', this);
        this.textInput.removeEventListener('blur', this);
        this.textCnt.removeEventListener('mouseup', this);
        this.textCnt.removeEventListener('mouseenter', this);
        this.textCnt.removeEventListener('mouseleave', this);
        this.expandBtn && this.expandBtn.removeEventListener('click', this);
        this.collapseBtn && this.collapseBtn.removeEventListener('click', this);
        this.editBtn && this.editBtn.removeEventListener('click', this);
        this.saveBtn && this.saveBtn.removeEventListener('click', this);
        this.cancelBtn && this.cancelBtn.removeEventListener('click', this);
    }

    /**
     * Обработка событий
     */
    handleEvent(event) {
        if (event.type === 'input') {
            // Если мы убираем мышь, а затем начинаем печатать текст - не закрываем
            clearTimeout(this._autoSaveTimer);

            return this.processValue();
        }

        if (event.type === 'blur') {
            if (this.state.autosave && event.target === this.textInput) {
                return this.save();
            }
        }

        if (event.type === 'click') {
            if (contains(this.expandBtn, event.target)) {
                return this.setExpanded();
            }
            if (contains(this.collapseBtn, event.target)) {
                return this.setCollapsed();
            }
            if (contains(this.editBtn, event.target)) {
                return this.setEditMode();
            }
            if (contains(this.saveBtn, event.target)) {
                return this.save();
            }
            if (contains(this.cancelBtn, event.target)) {
                return this.cancel();
            }
        }

        if (event.type === 'mouseup') {
            if (event.target !== this.textInput && !hasSelectedText(event.target) && !isHashtag(event.target)
                && !isStopEvents(event.target) && !isLinkInText(event.target)) {
                return this.toggleMode();
            } else {
                this.textCnt.removeAttribute('data-l');
            }
        }

        if (event.type === 'mouseenter') {
            // Вернули мышь, останавливаем автосэйв
            clearTimeout(this._autoSaveTimer);

            if (this.state.autoexpand) {
                return this.setExpanded();
            }
        }

        if (event.type === 'mouseleave') {
            if (this.state.editMode && this.state.autosave) {
                clearTimeout(this._autoSaveTimer);
                return this._autoSaveTimer = setTimeout(this._autoSave, AUTO_SAVE_DELAY);
            }

            if (this.state.autoexpand) {
                return this.setCollapsed();
            }
        }

        if (event.type === 'keydown' && event.target === this.textInput) {
            event.stopPropagation();

            if (event.keyCode === 13 && (event.ctrlKey || event.metaKey)) {
                return this.save();
            }

            if (event.keyCode === 27) {
                return this.cancel();
            }
        }
    }

    /**
     * Обработка изменения текста
     */
    processValue() {
        // Вставляем значение в скрытый слой, чтобы растягивать <textarea>
        this.textOutput.textContent = this.textInput.value;

        // Обновляем отображение для обработки ввода: анимации, счетчики...
        this.updateView({
            value: this.textInput.value
        });
    }

    /**
     * Переключение режимов
     */
    toggleMode() {
        if (this.state.editMode) {
            return this.setViewMode();
        }

        if (this.state.expandable && !this.state.noexpand && !this.state.expanded) {
            return this.setExpanded();
        }

        if (this.state.expanded && this.state.readOnly) {
            return this.setCollapsed();
        }

        if (!this.state.readOnly) {
            this.setEditMode();
        }
    }

    /**
     * Переключение на режим отображения
     * @param {Object} newState
     */
    setViewMode(newState) {
        this.updateView(Object.assign({}, newState, {
            editMode: false,
            expanded: false,
            expandable: false
        }));

        this._checkTextExpandable();
    }

    /**
     * Переключение на режим редактирования
     * @param {Object} newState
     */
    setEditMode(newState) {
        this.updateView(Object.assign({}, newState, {
            editMode: true
        }));
    }

    /**
     * Переключение на режим равернутого текста
     */
    setExpanded() {
        this.updateView({
            expanded: true
        });
    }

    /**
     * Переключение на режим свернутого текста
     */
    setCollapsed() {
        this.updateView({
            expanded: false
        });
    }

    /**
     * Отмена изменений в тексте
     */
    cancel() {
        this.setViewMode({
            value: this.state.originValue,
            error: ''
        });

        trigger(this.el, 'text-field-cancel');
    }

    /**
     * Сохранение изменений в тексте
     */
    save() {
        clearTimeout(this._autoSaveTimer);

        var state = this.state;
        var trimmed = state.value.trim();

        if (trimmed !== state.originValue) {

            if (this.saveUrl) {
                this.updateView({
                    loading: true,
                    value: trimmed
                });

                var self = this;
                var data = {};
                data[state.name] = state.value;

                ajax({
                    url: this.saveUrl,
                    data: data
                })
                    .then(function (resp) {
                        if (state.serverRendering) {
                            // Будем считать, что в полученных блоках всегда содержится наше текстовое поле,
                            // поэтому на клиенте никак не обрабатываем выход из стадии загрузки
                            updateBlockModelCallback(resp);
                        } else {
                            if (!resp.response) {
                                self.setViewMode({
                                    loading: false,
                                    error: '',
                                    originValue: state.value
                                });
                            } else {
                                self.setEditMode({
                                    loading: false,
                                    error: resp.response || ''
                                });
                            }
                        }
                    });
            }

            trigger(this.el, 'text-field-save', trimmed);
        } else {
            this.setViewMode({
                value: state.originValue
            });
        }
    }

    /**
     * Обновление отображения согласно состоянию
     * @param {Object} newState
     */
    updateView(newState) {
        var state = this._updateState(newState);

        if (this._prevLoading !== state.loading) {
            this._prevLoading = state.loading;

            if (state.loading) {
                this._disableControls();
            } else {
                this._enableControls();
            }
        }

        this.el.classList.toggle('__error', state.error);
        this.errorCnt.innerHTML = state.error;

        this.el.classList.toggle('__active', state.editMode);
        if (this._prevEditMode !== state.editMode) {
            this._prevEditMode = state.editMode;

            if (state.editMode) {
                this._updateActiveView(state);
            } else {
                this._updateInactiveView(state);
            }
        }
        if (this.counter && state.editMode) {
            this._updateCounterView(state);
        }

        this.el.classList.toggle('__expandable', state.expandable);
        this.el.classList.toggle('__expanded', state.expanded);

        if (state.logTargetOnExpand) {
            /* При expand проставляем таргет, при collapse убираем -> в раскрытом состоянии для ссылок таргет тоже пропадет */
            if (state.expanded) {
                this.textCnt.setAttribute('data-l', state.logTargetOnExpand);
            } else {
                this.textCnt.removeAttribute('data-l');
            }
        }

        this.textCnt.scrollTop = 0;

        // Прокидывам состояние в верхнеуровневый элемент для сохранения инкапсуляции
        this.el.state = state;
        this.el.value = state.value;

        trigger(this.el, 'text-field-update', state);
    }

    /**
     * Включаем контролы
     * @private
     */
    _enableControls() {
        this.textInput.removeAttribute('disabled');
        if (this.saveBtn) {
            this.saveBtn.removeAttribute('disabled');
        }
        if (this.cancelBtn) {
            this.cancelBtn.removeAttribute('disabled');
        }
    }

    /**
     * Выключаем контролы
     * @private
     */
    _disableControls() {
        this.textInput.setAttribute('disabled', 'disabled');
        if (this.saveBtn) {
            this.saveBtn.setAttribute('disabled', 'disabled');
        }
        if (this.cancelBtn) {
            this.cancelBtn.setAttribute('disabled', 'disabled');
        }
    }

    /**
     * Обновление отображения режима редактирования
     * @param {Object} state
     * @private
     */
    _updateActiveView(state) {
        this.textInput.value = state.value;
        if (state.autofocus) {
            this.textInput.focus();
            this.textInput.selectionStart = this.textInput.selectionEnd = this.textInput.value.length;
        }
        this.el.classList.remove('__placeholder');
    }

    /**
     * Обновление отображения режима просмотра
     * @param {Object} state
     * @private
     */
    _updateInactiveView(state) {
        var trimmed = state.value.trim();
        var isEmpty = trimmed.length === 0;
        var textEl = this.textOutput;

        if (state.serverRendering) {
            // для серверного рендеринга достаточно синхронизации разметки
            if (textEl.innerHTML !== state.serverHtml) {
                textEl.innerHTML = state.serverHtml;
            }
        } else {
            textEl.textContent = trimmed;
        }

        this.el.classList.toggle('__placeholder', isEmpty);
    }

    /**
     * Обновление отображения счетчика
     * @param {Object} state
     * @private
     */
    _updateCounterView(state) {
        this.counter.textContent = state.redesigned ? `${state.value.length}/${state.limit}` : state.leftChars;
        this.el.classList.toggle('__counter-threshold-warn', state.thresholdReached || state.redesigned);
        this.el.classList.toggle('__counter-overflow', state.limitExceeded);

        if (this.saveBtn) {
            if (state.limitExceeded) {
                this.saveBtn.setAttribute('disabled', 'disabled');
            } else {
                this.saveBtn.removeAttribute('disabled');
            }
        }
    }

    /**
     * Обновление стейта компоненты
     * @param {Object} newState
     * @returns {Object}
     * @private
     */
    _updateState(newState) {
        if (newState) {
            var state = this.state = Object.assign({}, this.state, newState);

            if (this.counter && state.editMode) {
                state.leftChars = state.limit - state.value.length;
                state.thresholdReached = state.limitThreshold !== -1 && state.leftChars < state.limitThreshold;
                state.limitExceeded = state.leftChars < 0;
            }
        }
        return state;
    }

    /**
     * Засекаем небольшой таймер, чтобы отработала анимация, и замеряем высоту текста.
     * При этом, перед проверкой, дожидаемся не нулевой высоты контейнера
     * @private
     */
    _checkTextExpandable() {
        var check = function () {
            if (this.textCnt.clientHeight) {
                this.updateView({expandable: this._isTextExpandable()});
            } else {
                setTimeout(check, UPDATE_EXPANDABLE_DELAY);
            }
        }.bind(this);

        check();
    }

    /**
     * Влез ли текст целиком или нужно добавить расхлопывалку
     * @returns {boolean}
     * @private
     */
    _isTextExpandable() {
        return this.textCnt.clientHeight < this.textOutput.offsetHeight;
    }
}


/**
 * Простая проверка на наличие выделения текста в элементе
 * @param elem
 * @returns {boolean}
 */
function hasSelectedText(elem) {
    var sel = window.getSelection();

    if (sel.rangeCount) {
        var range = sel.getRangeAt(0);

        return !range.collapsed && (contains(elem, range.startContainer) || contains(elem, range.endContainer));
    }

    return false;
}

/**
 * Альтернатива для `elem.contains(node)`, который почему-то не работает в IE
 * @param {Element} parent
 * @param {Node} node
 */
function contains(parent, node) {
    while (node && node !== document) {
        if (node === parent) {
            return true;
        }
        node = node.parentNode;
    }

    return false;
}

function isHashtag(node) {
    return node.nodeType === document.ELEMENT_NODE && node.getAttribute('uid') === 'hashtag';
}

function isStopEvents(node) {
    return node.nodeType === document.ELEMENT_NODE && node.getAttribute('uid') === 'stopEvents';
}

function isLinkInText(node) {
    return node.nodeType === document.ELEMENT_NODE && node.classList.contains('rev_cnt_a-in-txt');
}
